import React, { useState, useContext } from "react";
import { FormattedMessage, injectIntl } from "react-intl";

import moment from "moment";
import ReactTooltip from "react-tooltip";

import "wxrecall/components/SearchResults.css";
import SortControls from "wxrecall/components/SearchControls";

import DefaultStates from "wxrecall/util/DefaultStates";
import { AppContext } from "wxrecall/components/App";

import ColumnFilter from "wxrecall/components/SearchResults/ColumnFilter";
import ScrollToTopButton from "wxrecall/components/SearchResults/ScrollToTop";
import SearchQueryInfo from "wxrecall/components/SearchResults/SearchQueryInfo";
import ResultRows from "wxrecall/components/SearchResults/ResultRows";
import PrintedHeader from "wxrecall/components/SearchResults/PrintedHeader";
import { filterData, sortData } from "wxrecall/components/SearchResults/utils";

// useState is a hook! It lets you write functional components that can use state! OH MY. see: https://reactjs.org/docs/hooks-state.html
// You might have previously known these as “stateless components”. We’re now introducing the ability to use React state from these, so we prefer the name “function components”.
// Hooks don’t work inside classes. But you can use them instead of writing classes.

function parseSearchResults(searchResults) {
  // Given some search data return the number of results and the timestamp.
  const timestamp =
    searchResults.data && searchResults.meta
      ? searchResults.meta.now
      : undefined;
  const numResults = searchResults.data ? searchResults.data.length : undefined;
  return [timestamp, numResults];
}

function getSearchMessage(message, intl) {
  let className = `search-error alert alert-${message.type}`;
  let details = message.details ? message.details : {};
  return (
    <div key={message.message} className={className} role="alert">
      {intl.formatMessage(
        { id: message.message, defaultMessage: message.message },
        details
      )}
    </div>
  );
}

function getSearchMessages(searchData, intl) {
  if (searchData.meta && searchData.meta.messages) {
    return searchData.meta.messages.map(m => getSearchMessage(m, intl));
  }
  return null;
}

// This function preprocesses the metadata information to look as close as
// possible to how <MetaCell> looks like.
// This is incredibly stupid, and the whole filter mechanism needs to be
// refactored severely. This should be removed in favour of a more cohesive
// filtering mechanism in the future.
export function preProcessMetadata(tab, record) {
  const locations = record.locations
    .map(l => [l.location.description, l.location.label].join(" "))
    .join(" ");
  const type = record.translated.type;
  var timeMeta = "";
  if (tab === "bulletin") {
    timeMeta = moment(record.startValidity).format("MMM DD - H:mm");
  }
  // The field should end up looking like "CYOW CYYZ NOTAM" for example
  record._preProcessed = [type, locations, timeMeta].join(" ");
  return record;
}

function getResultRows(
  searchData,
  intl,
  tab,
  sortOrder,
  showMeta,
  selectedFilters,
  setSelectedFilters,
  bulletinFilter,
  metaFilter,
  setBulletinFilter,
  bulletinFilterType,
  toggleBulletinFilterType,
  setMetaFilter,
  collapseDuplicates,
  networkManager
) {
  // Return a ResultRows component and the number of rows.
  const sortedData = sortData(searchData, sortOrder);

  const selectedPredefinedFilters = [];
  selectedFilters.forEach(selectedFilter => {
    if (selectedFilter) {
      selectedPredefinedFilters.push({
        filter: selectedFilter.pattern,
        column: "filterText",
        invertTestResult: true,
        isRegex: true
      });
    }
  });

  const [data, resultCount] = filterData(sortedData, collapseDuplicates, [
    {
      filter: bulletinFilter,
      column: "filterText",
      invertTestResult: bulletinFilterType === "exclude"
    },
    {
      filter: metaFilter,
      column: "_preProcessed",
      preProcessor: preProcessMetadata.bind(this, tab)
    },
    ...selectedPredefinedFilters
  ]);

  const filters = [
    <ColumnFilter
      id="metadata-filter"
      value={metaFilter}
      onChange={setMetaFilter}
    />,
    <ColumnFilter
      id="bulletin-filter"
      value={bulletinFilter}
      onChange={setBulletinFilter}
      filterType={bulletinFilterType}
      onToggle={toggleBulletinFilterType}
    />
  ];
  const resultRows = (
    <ResultRows
      key="searchResults"
      searchParams={searchData.searchParameters}
      showMeta={showMeta}
      columnFilters={filters}
      meta={searchData.meta}
      data={data}
      tab={tab}
      networkManager={networkManager}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
    />
  );
  return [resultRows, resultCount];
}

function SearchResults(props) {
  const appContext = useContext(AppContext);
  const [bulletinFilter, setBulletinFilter] = useState("");
  const [bulletinFilterType, setBulletinFilterType] = useState("include");
  const toggleBulletinFilterType = () => {
    setBulletinFilterType(
      bulletinFilterType === "include" ? "exclude" : "include"
    );
  };

  const [metaFilter, setMetaFilter] = useState("");
  const [showMeta, setShowMeta] = useState(DefaultStates.showMeta);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [timestamp, totalResults] = parseSearchResults(props.searchData);

  let results = null; // result rows
  let numDisplayedResults = 0;

  const searchMessages = getSearchMessages(props.searchData, props.intl);
  // Default states for searchData if it is not defined (load and reset)
  if (!props.searchData.data) {
    props.searchData.data = [];
  }

  if (!props.searchData.searchParameters) {
    props.searchData.searchParameters = {
      tags: []
    };
  }

  if (props.searchData.data.length === 0) {
    // reset the filter value when we execute a new search
    if (bulletinFilter !== "") {
      setBulletinFilter("");
    }
    if (bulletinFilterType !== "include") {
      setBulletinFilterType("include");
    }
  }

  if (props.searchData.data.length === 0 && metaFilter !== "") {
    setMetaFilter("");
  }

  if (props.searchData.data.length) {
    [results, numDisplayedResults] = getResultRows(
      props.searchData,
      props.intl,
      props.tab,
      props.sortOrder,
      showMeta,
      props.selectedFilters,
      props.setSelectedFilters,
      bulletinFilter,
      metaFilter,
      setBulletinFilter,
      bulletinFilterType,
      toggleBulletinFilterType,
      setMetaFilter,
      props.collapseDuplicates,
      props.networkManager
    );
  }

  const onScroll = appContext.mobile
    ? null
    : e => {
        const t = e.target;
        if (!t) return;

        // Leave a bit of buffer for the header, once we scroll past it show the scrollToTop button.
        if (showScrollToTop === false && t.scrollTop > 100) {
          setShowScrollToTop(true);
        } else if (showScrollToTop === true && t.scrollTop < 100) {
          setShowScrollToTop(false);
        }
      };

  return (
    <React.Fragment>
      <div className="search-results" onScroll={onScroll}>
        {searchMessages}
        <PrintedHeader />
        <SortControls
          sortOrder={props.sortOrder}
          onSort={props.onSort}
          showMeta={showMeta}
          handleCheckMeta={setShowMeta}
          tab={props.tab}
        />
        <SearchQueryInfo
          key="SearchControls"
          timestamp={timestamp || ""}
          numTotalRecords={totalResults || 0}
          numDisplayedRecords={numDisplayedResults || 0}
        />
        {results}
      </div>
      {(showScrollToTop || appContext.mobile) && (
        <ScrollToTopButton mobile={appContext.mobile} />
      )}
      <ReactTooltip id="scroll_button_tooltip" place="left" effect="solid">
        <FormattedMessage
          id="alpha.scrollToTop"
          defaultMessage="Scroll To Top"
        />
      </ReactTooltip>
    </React.Fragment>
  );
}

export default injectIntl(SearchResults);
